import React, { useState } from 'react';
import { Wrapper, useBreadcrumbRoutes, useUpdate } from '@maxtropy/components';
import dayjs from 'dayjs';
import DimensionSelector from './components/DimensionSelector';
import IndicatorCards from './components/IndicatorCards';
import TrendTimeControls from './components/TrendTimeControls';
import { GasEmissionChart, WaterEmissionChart } from './components/OverallEmissionChart';
import DeviceDetail from './components/DeviceDetail';
import RecalculateModal from './components/RecalculateModal';
import { getDefaultTimeValue, getDefaultDateRange } from './utils';
import { DashboardDimension, TrendGranularity } from './types';
import SubTitle from '@/components/SubTitle';
import { useRequest } from 'ahooks';
import { apiV2EnvMonitorConfigGetPost } from '@maxtropy/device-customer-apis-v2';

const EnvMonitorKanban: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const routes = [...(breadcrumbRoutes?.routes ?? [])];

  // 全局维度状态
  const [dimension, setDimension] = useState<DashboardDimension>('MONTH');
  const [timeValue, setTimeValue] = useState<string>(() => getDefaultTimeValue('MONTH'));
  const [update, updateFn] = useUpdate();

  // 趋势图共用时间控制
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>(TrendGranularity.HOUR);
  const [trendDateRange, setTrendDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() =>
    getDefaultDateRange(TrendGranularity.HOUR)
  );

  // 弹窗状态
  const [recalculateModalOpen, setRecalculateModalOpen] = useState(false);

  const { data: deviceListData } = useRequest(apiV2EnvMonitorConfigGetPost);

  const handleDimensionChange = (d: DashboardDimension) => {
    setDimension(d);
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
  };
  const handleTrendGranularityChange = (g: TrendGranularity) => {
    setTrendGranularity(g);
    setTrendDateRange(getDefaultDateRange(g));
  };

  const handleTrendDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setTrendDateRange(dates);
  };

  return (
    <Wrapper routes={routes}>
      <div>
        <DimensionSelector
          dimension={dimension}
          timeValue={timeValue}
          refreshKey={update}
          onDimensionChange={handleDimensionChange}
          onTimeChange={handleTimeChange}
          onOpenRecalculate={() => setRecalculateModalOpen(true)}
        />

        <IndicatorCards
          gasDevices={deviceListData?.exhaustDevices}
          waterDevices={deviceListData?.wastewaterDevices}
          refreshKey={update}
        />

        <TrendTimeControls
          granularity={trendGranularity}
          dateRange={trendDateRange}
          onGranularityChange={handleTrendGranularityChange}
          onDateRangeChange={handleTrendDateRangeChange}
        />
        <GasEmissionChart refreshKey={update} granularity={trendGranularity} dateRange={trendDateRange} />
        <WaterEmissionChart refreshKey={update} granularity={trendGranularity} dateRange={trendDateRange} />
        <SubTitle title="监测设备指标">
          <DeviceDetail
            title="废气设备监测详情"
            deviceType="GAS"
            devices={deviceListData?.exhaustDevices}
            refreshKey={update}
          />

          <DeviceDetail
            title="废水设备监测详情"
            deviceType="WATER"
            devices={deviceListData?.wastewaterDevices}
            refreshKey={update}
          />
        </SubTitle>
      </div>
      <RecalculateModal
        open={recalculateModalOpen}
        updateFn={updateFn}
        onCancel={() => setRecalculateModalOpen(false)}
      />
    </Wrapper>
  );
};

export default EnvMonitorKanban;
